/**
 * Slider - Range slider with label and value display
 *
 * Use with React Hook Form (requires Controller):
 * <Controller
 *   name="volume"
 *   control={control}
 *   render={({ field }) => (
 *     <Slider
 *       label="Volume"
 *       min={0}
 *       max={100}
 *       value={field.value}
 *       onChange={field.onChange}
 *     />
 *   )}
 * />
 *
 * Or standalone:
 * <Slider label="Brightness" min={0} max={100} value={brightness} onChange={setBrightness} />
 */

import { forwardRef } from 'react';

import { cn } from '../utils';

export interface SliderProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value'
> {
  /** Slider label */
  label: string;
  /** Hide the label visually (still available for screen readers) */
  hideLabel?: boolean;
  /** Current value */
  value?: number;
  /** Change handler - receives number value */
  onChange?: (value: number) => void;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment */
  step?: number;
  /** Show current value */
  showValue?: boolean;
  /** Format value for display */
  formatValue?: (value: number) => string;
  /** Optional helper text */
  hint?: string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      label,
      hideLabel,
      value = 0,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      showValue = true,
      formatValue = (v) => String(v),
      hint,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const percentage = ((value - min) / (max - min)) * 100;
    const showHeader = !hideLabel || showValue;

    return (
      <div className={className}>
        {showHeader && (
          <div className="flex items-center justify-between">
            <label
              className={cn(
                'block text-sm font-medium text-gray-700 dark:text-zinc-300',
                hideLabel && 'sr-only'
              )}
            >
              {label}
            </label>
            {showValue && (
              <span className="text-sm font-medium text-gray-500 dark:text-zinc-400">
                {formatValue(value)}
              </span>
            )}
          </div>
        )}
        <div className={cn('relative', showHeader && 'mt-2')}>
          <input
            ref={ref}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange?.(Number(e.target.value))}
            disabled={disabled}
            className={cn(
              'w-full appearance-none bg-transparent cursor-pointer',
              'focus:outline-none',
              '[&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-gray-300 dark:[&::-webkit-slider-runnable-track]:bg-zinc-700',
              '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:-mt-1.5 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110',
              '[&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-gray-300 dark:[&::-moz-range-track]:bg-zinc-700',
              '[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:transition-transform [&::-moz-range-thumb]:hover:scale-110',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, transparent ${percentage}%)`,
            }}
            {...props}
          />
        </div>
        {hint && <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{hint}</p>}
      </div>
    );
  }
);

Slider.displayName = 'Slider';
